import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import { normalizeConfig, type ConsentColors, type ConsentConfig, type ConsentService } from './types';
import { translations } from './translations';
import { catalogFor } from './service-catalog';
import { setupContentBlocker, syncContentBlocker, setupLazyEmbedBlocker, lazyEmbedOnConsentChange, EMBED_CATEGORY } from './content-blocker';
import { setupFormGuard, formGuardOnConsentChange } from './form-guard';

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Banner/UI language. Rule: German is shown ONLY when the page is not explicitly
 * English AND the visitor's browser prefers German. Every other visitor gets English
 * — so a German site opened with an English (or any non-German) browser shows the
 * banner in English.
 *   - <html lang="en…">  → en   (dedicated English page, e.g. WPML/Polylang /en/)
 *   - otherwise          → browser: German preference → de, else → en
 *   - browser undetectable → site default (fallback)
 */
function resolveLang(fallback: 'de' | 'en'): 'de' | 'en' {
  const page = (document.documentElement.lang || '').toLowerCase();
  if (page.indexOf('en') === 0) return 'en';
  const navLangs =
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || ''];
  const known = navLangs.filter(Boolean);
  if (!known.length) return fallback;
  return known.some((l) => l.toLowerCase().indexOf('de') === 0) ? 'de' : 'en';
}

/** Run after the full DOM is parsed (footer scripts/forms are then available). */
function onDomReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

/**
 * Build the eRecht24-style service "card" used as the CookieConsent service label
 * (HTML is allowed). Per-site fields (description, company, links) come from
 * Borlabs; purposes/technologies/data fall back to the curated catalog.
 * Note: CookieConsent service labels are not per-language, so cards render in the
 * configured default language.
 */
function buildServiceLabel(s: ConsentService, lang: 'de' | 'en'): string {
  const card = translations[lang].card;
  const cat = catalogFor(s.key, lang);
  const purposes = s.purposes ?? cat?.purposes ?? [];
  const technologies = s.technologies ?? cat?.technologies ?? [];
  const data = s.data ?? cat?.data ?? [];

  // Inline styles only: CookieConsent inserts the label via innerHTML, but external
  // class-based CSS isn't reliably applied inside the modal, so we self-contain it.
  const chip =
    'display:inline-block;padding:.12em .6em;margin:0 .3em .3em 0;border:1px solid rgba(0,0,0,.15);border-radius:1em;background:#fff;font-size:.92em;line-height:1.6';
  const heading = 'display:block;font-weight:600;margin:.7em 0 .25em;opacity:.65;font-size:.82em';

  const chips = (arr: string[]): string =>
    `<div style="margin-top:.15em">${arr.map((x) => `<span style="${chip}">${escapeHtml(x)}</span>`).join('')}</div>`;
  const row = (title: string, body: string): string =>
    `<div style="margin-bottom:.2em"><span style="${heading}">${title}</span>${body}</div>`;

  // Prefer the translated catalog description; fall back to the Borlabs text.
  const description = cat?.description ?? s.description;
  const parts: string[] = [];
  if (description) parts.push(row(card.description, `<div style="font-size:.85em;line-height:1.5">${escapeHtml(description)}</div>`));
  if (s.company) parts.push(row(card.company, `<div style="font-size:.85em;line-height:1.5">${escapeHtml(s.company)}</div>`));
  const links: string[] = [];
  if (s.privacyUrl) links.push(`<a href="${escapeHtml(s.privacyUrl)}" target="_blank" rel="noopener" style="text-decoration:underline">${card.privacy}</a>`);
  if (s.cookieUrl) links.push(`<a href="${escapeHtml(s.cookieUrl)}" target="_blank" rel="noopener" style="text-decoration:underline">${card.cookies}</a>`);
  if (links.length) parts.push(`<div style="margin:.5em 0;font-size:.85em">${links.join(' · ')}</div>`);
  if (purposes.length) parts.push(row(card.purposes, chips(purposes)));
  if (technologies.length) parts.push(row(card.technologies, chips(technologies)));
  if (data.length) parts.push(row(card.data, chips(data)));

  let html = `<span style="font-weight:600">${escapeHtml(s.name)}</span>`;
  if (parts.length) {
    html +=
      `<details style="margin-top:.45em">` +
      `<summary style="cursor:pointer;font-size:.85em;color:#0a58ca;user-select:none">${card.details}</summary>` +
      `<div style="margin-top:.55em;padding:.7em .85em;border:1px solid #e2e8f0;border-radius:.5em;background:#f8fafc">${parts.join('')}</div>` +
      `</details>`;
  }
  return html;
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

// Mirrors the gtag stub from headSnippet(). If the head bootstrap already ran,
// this pushes to the same dataLayer; if not (e.g. banner loaded standalone), it
// still works.
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/** Push the current accepted-category state into Consent Mode v2 signals. */
function syncConsentMode(): void {
  const granted = (category: string): 'granted' | 'denied' =>
    CookieConsent.acceptedCategory(category) ? 'granted' : 'denied';

  gtag('consent', 'update', {
    analytics_storage: granted('analytics'),
    ad_storage: granted('ads'),
    ad_user_data: granted('ads'),
    ad_personalization: granted('ads'),
    functionality_storage: granted('functional'),
    personalization_storage: granted('functional'),
  });

  // Drive the GTM containers' consent flow. Across the (normalized) fleet the
  // consent-update trigger is `cmplz_event_preferences` — that's what re-fires the
  // Google tag after consent. So our plugin takes over the CMP's two jobs:
  //   1. gtag('consent','update', …) above (what Complianz/Borlabs JS used to do),
  //   2. push `cmplz_event_preferences` to re-fire the GA4/Ads config tag.
  // `borlabs_consent_saved` additionally drives per-service tags (e.g. the Meta
  // Pixel in some containers), which gate on the Borlabs Consent Variable —
  // `BorlabsCookie.Consents.hasConsent('<service-key>')`, which we emulate.
  // `consent_update` is our own neutral event for any custom triggers.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'consent_update' });
  window.dataLayer.push({ event: 'borlabs_consent_saved' });
  window.dataLayer.push({ event: 'cmplz_event_preferences' });
}

/** Sync Consent Mode AND the content blocker — used on every consent event. */
function afterConsent(): void {
  syncConsentMode();
  syncContentBlocker(CookieConsent.acceptedCategory(EMBED_CATEGORY));
  lazyEmbedOnConsentChange();
  formGuardOnConsentChange();
  if (debugCfg) renderDebugPanel();
}

/** Persist functional consent (e.g. when a user clicks an embed placeholder). */
function acceptFunctional(): void {
  const prefs = CookieConsent.getUserPreferences();
  const accepted: string[] = (prefs && prefs.acceptedCategories) || [];
  if (accepted.indexOf(EMBED_CATEGORY) === -1) {
    CookieConsent.acceptCategory([...accepted, EMBED_CATEGORY]);
  }
}

/** Map brand colors onto CookieConsent v3 CSS custom properties. */
function applyColors(colors: ConsentColors): void {
  const root = document.documentElement;
  const set = (name: string, value: string) => root.style.setProperty(name, value);
  const accept = colors.accept || colors.primary;

  // Highlighted "accept all" button (the primary CTA).
  set('--cc-btn-primary-bg', accept);
  set('--cc-btn-primary-border-color', accept);
  set('--cc-btn-primary-hover-bg', colors.accent);
  set('--cc-btn-primary-hover-border-color', colors.accent);
  set('--cc-btn-primary-text', '#ffffff');
  set('--cc-btn-primary-hover-text', '#ffffff');

  // Muted secondary buttons ("Necessary only" / "Save") so the accept CTA stands out.
  set('--cc-btn-secondary-bg', '#eef1f4');
  set('--cc-btn-secondary-border-color', '#eef1f4');
  set('--cc-btn-secondary-color', colors.text);
  set('--cc-btn-secondary-hover-bg', '#e2e8f0');
  set('--cc-btn-secondary-hover-border-color', '#e2e8f0');
  set('--cc-btn-secondary-hover-color', colors.text);

  set('--cc-toggle-on-bg', colors.secondary);
  set('--cc-cookie-category-block-bg-hover', '#f0f4f8');
}

/**
 * Configure and show the consent banner. Safe to call after DOM is ready.
 * Returns the resolved config (useful for tests/debugging).
 */
/**
 * True if the current page IS the privacy policy or imprint page. There the banner
 * must NOT block reading — those pages must stay fully accessible without consenting
 * (legal requirement). We still init CookieConsent (floating button etc.), we just
 * don't auto-show the modal or dim the page.
 */
function isLegalPage(links?: { privacy?: string; imprint?: string }): boolean {
  if (!links) return false;
  const here = window.location.pathname.replace(/\/+$/, '');
  for (const url of [links.privacy, links.imprint]) {
    if (!url) continue;
    try {
      const p = new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
      if (p && p === here) return true;
    } catch (e) {
      /* ignore malformed URL */
    }
  }
  return false;
}

let started = false;
let debugCfg: ConsentConfig | null = null;

export function init(input: Partial<ConsentConfig>): ConsentConfig {
  const config = normalizeConfig(input);
  // Idempotent: the WP plugin's inline call AND the bundle self-init (see index.ts)
  // may both fire under JS optimizers; only the first actually runs.
  if (started) return config;
  started = true;
  applyColors(config.colors);
  const uiLang = resolveLang(config.lang);
  const legal = isLegalPage(config.links);
  if (config.debug) debugCfg = config;

  // Group individual services (migrated from Borlabs) under their category, in the
  // shape CookieConsent v3 expects: { serviceKey: { label } }.
  const servicesByCat: Record<string, Record<string, { label: string }>> = {};
  for (const s of config.services ?? []) {
    if (!servicesByCat[s.category]) servicesByCat[s.category] = {};
    servicesByCat[s.category][s.key] = { label: buildServiceLabel(s, uiLang) };
  }
  const hasSvc = (c: string): boolean => !!servicesByCat[c] && Object.keys(servicesByCat[c]).length > 0;

  // A category appears if its base condition holds OR it lists any service.
  const showAds = config.categories.ads || hasSvc('ads');
  const showFunctional = config.categories.calltracking || !!config.contentBlocker || hasSvc('functional');

  const withServices = (cat: string, base: Record<string, unknown>): Record<string, unknown> =>
    servicesByCat[cat] ? { ...base, services: servicesByCat[cat] } : base;

  const categories: Record<string, unknown> = {
    necessary: withServices('necessary', { enabled: true, readOnly: true }),
    analytics: withServices('analytics', {}),
  };
  if (showAds) categories.ads = withServices('ads', {});
  if (showFunctional) categories.functional = withServices('functional', {});

  // Bundle both languages; the active one is decided by resolveLang() (browser-aware,
  // see uiLang above) and passed as CookieConsent's `default`. No autoDetect — we do
  // the resolution ourselves so a German page opened with a non-German browser still
  // shows English.
  const supported: Array<'de' | 'en'> = ['de', 'en'];
  const langTranslations: Record<string, unknown> = {};
  for (const lang of supported) {
    const t = translations[lang];
    // Banner footer: privacy policy + imprint links (always reachable, legally required).
    const linkParts: string[] = [];
    // The site's OWN privacy/imprint pages → open in the SAME tab (no _blank).
    // On those pages the banner no longer auto-shows (see isLegalPage), so they
    // stay fully readable.
    if (config.links?.privacy) {
      linkParts.push(`<a href="${config.links.privacy}">${t.links.privacy}</a>`);
    }
    if (config.links?.imprint) {
      linkParts.push(`<a href="${config.links.imprint}">${t.links.imprint}</a>`);
    }
    const footer = linkParts.join(' · ');
    const sections: unknown[] = [
      { title: t.sections.intro.title, description: t.sections.intro.description },
      { title: t.sections.necessary.title, description: t.sections.necessary.description, linkedCategory: 'necessary' },
      { title: t.sections.analytics.title, description: t.sections.analytics.description, linkedCategory: 'analytics' },
    ];
    if (showAds) {
      sections.push({ title: t.sections.ads.title, description: t.sections.ads.description, linkedCategory: 'ads' });
    }
    if (showFunctional) {
      sections.push({ title: t.sections.functional.title, description: t.sections.functional.description, linkedCategory: 'functional' });
    }
    langTranslations[lang] = {
      consentModal: footer ? { ...t.consentModal, footer } : t.consentModal,
      preferencesModal: { ...t.preferencesModal, sections },
    };
  }

  // Run on DOMContentLoaded: CookieConsent's manageScriptTags only enables managed
  // <script> tags that exist when it runs. reCAPTCHA's script and the forms live
  // in the footer, so running inline (too early) means CC never sees the script
  // and never enables it on consent. Deferring keeps the HTML cacheable (always
  // blocked server-side) while consent is resolved entirely client-side.
  onDomReady(() => {
    CookieConsent.run({
      // Block the page (backdrop) until the user decides, like Borlabs — EXCEPT on
      // the privacy/imprint page, which must stay readable without consenting.
      disablePageInteraction: !legal,
      autoShow: !legal,
      guiOptions: {
        consentModal: { layout: 'box wide', position: 'middle center' },
        preferencesModal: { layout: 'box' },
      },
      // Native script blocking: <script type="text/plain" data-category data-service>
      // is loaded only after consent and tied to the matching service toggle.
      manageScriptTags: true,
      categories: categories as never,
      language: {
        default: uiLang,
        translations: langTranslations,
      },
      // onConsent fires on the first decision AND on every page load once consent
      // exists; onChange fires when the user later edits preferences. Sync on both.
      // NOT onFirstConsent: it fires together with onConsent on the first decision,
      // which pushed the whole update block twice (double consent_update /
      // cmplz_event_preferences → e.g. Meta Pixel "Duplicate Pixel ID").
      onConsent: afterConsent,
      onChange: afterConsent,
    } as never);

    if (config.contentBlocker) {
      setupContentBlocker(uiLang, { acceptCategory: acceptFunctional });
      syncContentBlocker(CookieConsent.acceptedCategory(EMBED_CATEGORY));
      setupLazyEmbedBlocker(uiLang);
    }
    setupFormGuard(uiLang, config.links?.privacy);

    if (config.settingsButton !== false) {
      renderSettingsButton(translations[uiLang].settingsButton, config.colors);
    }
    if (config.debug) renderDebugPanel();
  });

  return config;
}

/** Re-open the preferences modal (wire to a footer "Cookie-Einstellungen" link). */
export function showPreferences(): void {
  CookieConsent.showPreferences();
}

/**
 * Floating button so visitors can reopen the consent preferences any time. Also,
 * CookieConsent natively handles any element with data-cc="show-preferencesModal".
 */
/** Perceived (sRGB) luminance test — true for light colors like an imported #FFFFFF. */
function isLightColor(hex: string): boolean {
  const m = /^#?([0-9a-fA-F]{6})$/.exec( ( hex || '' ).trim() );
  if ( ! m ) return false;
  const n = parseInt( m[1], 16 );
  const r = ( n >> 16 ) & 255;
  const g = ( n >> 8 ) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 186;
}

function renderSettingsButton(label: string, colors: { primary: string; accept: string; text: string }): void {
  if (document.getElementById('occ-settings-btn')) return;
  // A light 'primary' (e.g. #FFFFFF imported from Borlabs) would render a white icon
  // on a white circle → invisible. Pick the first sufficiently dark brand color so the
  // floating button is always legible; the icon color follows the chosen background.
  const bg = [colors.primary, colors.accept, colors.text, '#012060'].find((c) => !isLightColor(c)) || '#012060';
  const fg = isLightColor(bg) ? '#18181B' : '#ffffff';
  const btn = document.createElement('button');
  btn.id = 'occ-settings-btn';
  btn.type = 'button';
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.innerHTML =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9"></circle><circle cx="9" cy="9" r="1"></circle><circle cx="15.5" cy="11" r="1"></circle><circle cx="10" cy="15" r="1"></circle><circle cx="15" cy="15.5" r="1"></circle></svg>';
  btn.style.cssText =
    'position:fixed;left:1rem;bottom:1rem;z-index:2147483646;width:44px;height:44px;border:none;border-radius:50%;' +
    `background:${bg};color:${fg};cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;padding:0`;
  btn.addEventListener('click', () => CookieConsent.showPreferences());
  document.body.appendChild(btn);
}

/** Consent state of a category, for the debug panel. */
function catState(category: string): { blocked: boolean; label: string } {
  if (category === 'necessary') return { blocked: false, label: 'immer aktiv' };
  const ok = CookieConsent.acceptedCategory(category);
  return { blocked: !ok, label: ok ? 'zugelassen' : 'geblockt' };
}

/**
 * Admin-only transparency panel: lists — for the CURRENT consent — which categories,
 * services and page scripts/embeds are allowed vs blocked. Gated by config.debug
 * (admins / ?occ_debug=1 only, never visitors). Re-rendered on every consent change.
 */
function renderDebugPanel(): void {
  const cfg = debugCfg;
  if (!cfg) return;
  let panel = document.getElementById('occ-debug');
  if (panel && panel.getAttribute('data-hidden') === '1') return;
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'occ-debug';
    panel.style.cssText =
      'position:fixed;right:1rem;bottom:1rem;z-index:2147483647;width:330px;max-width:92vw;max-height:72vh;overflow:auto;' +
      'background:#0f172a;color:#e2e8f0;font:12px/1.5 system-ui,-apple-system,sans-serif;border-radius:.55em;box-shadow:0 6px 26px rgba(0,0,0,.45)';
    document.body.appendChild(panel);
  }

  const row = (name: string, meta: string, s: { blocked: boolean; label: string }): string =>
    '<div style="display:flex;justify-content:space-between;gap:.6em;padding:.24em 0;border-top:1px solid #1e293b">' +
    `<span>${escapeHtml(name)}${meta ? ` <span style="opacity:.5">${escapeHtml(meta)}</span>` : ''}</span>` +
    `<span style="font-weight:600;white-space:nowrap;color:${s.blocked ? '#f87171' : '#4ade80'}">${s.blocked ? '⛔' : '✅'} ${s.label}</span></div>`;
  const head = (t: string): string =>
    `<div style="font-weight:700;margin:.7em 0 .1em;opacity:.75;font-size:11px;letter-spacing:.05em;text-transform:uppercase">${t}</div>`;

  let cats = '';
  for (const c of ['necessary', 'analytics', 'ads', 'functional']) cats += row(c, '', catState(c));

  const seen: Record<string, boolean> = {};
  let svc = '';
  for (const s of cfg.services ?? []) {
    if (seen[s.key]) continue;
    seen[s.key] = true;
    svc += row(s.name || s.key, s.category, catState(s.category));
  }

  let page = '';
  document.querySelectorAll('script[data-category]').forEach((el) => {
    const blocked = (el.getAttribute('type') || '') === 'text/plain';
    const src = (el.getAttribute('src') || 'inline').replace(/^https?:\/\//, '').slice(0, 44);
    page += row('script ' + src, el.getAttribute('data-category') || '', { blocked, label: blocked ? 'geblockt' : 'geladen' });
  });
  document.querySelectorAll('.occb-embed[data-service]').forEach((el) => {
    page += row('embed ' + (el.getAttribute('data-service') || ''), EMBED_CATEGORY, { blocked: true, label: 'geblockt' });
  });

  panel.innerHTML =
    '<div style="display:flex;justify-content:space-between;align-items:center;background:#1e293b;padding:.5em .7em;border-radius:.55em .55em 0 0;position:sticky;top:0">' +
    '<strong>OMmatic Consent — Status</strong>' +
    '<button type="button" id="occ-debug-x" aria-label="Schließen" style="background:none;border:none;color:#94a3b8;font-size:17px;cursor:pointer;line-height:1;padding:0 .2em">×</button></div>' +
    '<div style="padding:.2em .75em .8em">' +
    `<div style="opacity:.55;margin:.35em 0">nur für Admins sichtbar${cfg.gtmId ? ' · GTM ' + escapeHtml(cfg.gtmId) : ''}</div>` +
    head('Kategorien') + cats +
    (svc ? head('Dienste') + svc : '') +
    (page ? head('Auf dieser Seite') + page : '') +
    '</div>';

  const x = document.getElementById('occ-debug-x');
  if (x) {
    x.addEventListener('click', () => {
      panel!.setAttribute('data-hidden', '1');
      panel!.remove();
    });
  }
}
